import { useParams, Link, useSearchParams } from "react-router-dom";
import {
  Alert,
  AlertDescription,
  Card,
  CardContent,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Button,
  Typography,
} from "@altimateai/lego";
import { useFeatureFlag } from "@/helpers/useFeatureFlag";
import { Stack } from "@altimateai/lego";
import { CopyButton } from "@/components/copyButton";
import { CodeSQLEditor } from "@/components/codeEditor";
import { RelativeComponentLoader } from "@/components/loader";
import { BackButton } from "@/components/button";
import { ColorInsightTags } from "@/components/tags";
import { SqlLineage } from "@/components/lineage";
import { IconInfoCircle } from "@tabler/icons-react";
import { useDatabricksQueryDetail, useDatabricksQuerySqlLineage } from "../hooks";
import { QueryMetricsGrid } from "./QueryMetricsGrid";
import DatabricksQueryOpportunities from "./DatabricksQueryOpportunities";
import { PlaygroundUserViewEntities } from "@/modules/playground";
import { useAskAI } from "@/pages/sql-warehouses/hooks/useAskAI";
import { useBreadcrumb } from "@/components/breadcrumbs";

const DatabricksQueryDetailPage = () => {
  // Route uses :queryId param, which contains the statement_id value
  const { queryId: statementId } = useParams<{ queryId: string }>();
  const [searchParams] = useSearchParams();
  const enableGlobalPlayground = useFeatureFlag("gp");

  const activeTab = searchParams.get("tab") || "code";

  const { data, isLoading, error } = useDatabricksQueryDetail(
    statementId || "",
    Boolean(statementId)
  );

  useBreadcrumb(statementId ?? "Query", {
    defaultParents: [{ label: "Queries", pathname: "/queries" }],
  });

  // Only fetch SQL lineage when on the sql-visualizer tab
  const { data: sqlLineageData, isLoading: isLineageLoading } = useDatabricksQuerySqlLineage(
    statementId || "",
    Boolean(statementId) && activeTab === "sql-visualizer"
  );

  const { handleAskAI } = useAskAI({
    resourceType: "query",
    resourceId: statementId || "",
    resourceName: statementId,
    entityType: PlaygroundUserViewEntities.DATABRICKS_QUERY,
    context: {
      queryId: statementId,
      statementType: data?.query?.statement_type,
      userName: data?.query?.user_name,
    },
  });

  // Helper function to generate search params with tab value
  const getSearchWithTab = (tab: string) => {
    const params = new URLSearchParams(searchParams);
    params.set("tab", tab);
    return params.toString();
  };

  if (isLoading) {
    return (
      <Stack align="center" justify="center" className="al-h-full">
        <RelativeComponentLoader label="Loading query details..." />
      </Stack>
    );
  }

  if (error || !data) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          {error instanceof Error ? error.message : "Failed to load query details"}
        </AlertDescription>
      </Alert>
    );
  }

  const query = data.query;
  const insightTags = query.insights || [];

  const codeEditor = (
    <CodeSQLEditor
      value={query.statement_text || "-- No SQL text available"}
      height="300px"
      lineNumbers={true}
      showCopy={true}
      minimap={false}
    />
  );

  return (
    <div className="al-p-4">
      <Card>
        <CardContent className="al-pt-4">
          <Stack direction="column" gap="md">
            {/* Header with Query ID */}
            <Stack align="center" gap="sm">
              <BackButton defaultUrl="/queries" />
              <Typography size="lg" weight="medium">
                Query ID:
              </Typography>
              <Typography
                size="lg"
                weight="medium"
                className="al-text-primary al-truncate al-max-w-[400px]"
                title={statementId}
              >
                {statementId}
              </Typography>
              <CopyButton value={statementId || ""} />
              <div className="al-flex-1" />
              {enableGlobalPlayground && (
                <Button variant="outline" onClick={() => handleAskAI()}>
                  Ask AI
                </Button>
              )}
            </Stack>

            {/* Compact horizontal metrics row */}
            <QueryMetricsGrid query={query} />

            {/* Insight tags */}
            {insightTags.length > 0 && (
              <div className="al-py-2">
                <ColorInsightTags
                  tags={insightTags}
                  labelFn={t => t.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                />
              </div>
            )}

            {/* SQL Visualizer / Code Tabs */}
            <Tabs value={activeTab} className="al-w-full al-mt-4">
              <TabsList>
                <TabsTrigger value="sql-visualizer" asChild>
                  <Link to={{ search: getSearchWithTab("sql-visualizer") }}>SQL Visualizer</Link>
                </TabsTrigger>
                <TabsTrigger value="code" asChild>
                  <Link to={{ search: getSearchWithTab("code") }}>Code</Link>
                </TabsTrigger>
              </TabsList>
              <TabsContent value="sql-visualizer">
                {isLineageLoading ? (
                  <Stack align="center" justify="center" className="al-h-[50vh]">
                    <RelativeComponentLoader label="Loading SQL lineage..." />
                  </Stack>
                ) : sqlLineageData?.is_lineage ? (
                  <div className="al-relative">
                    {sqlLineageData.is_error && (
                      <Stack
                        gap="xs"
                        align="center"
                        className="al-absolute al-top-2 al-left-2 al-z-10 al-bg-yellow-50 al-border al-border-yellow-200 al-rounded al-px-3 al-py-2 al-text-sm al-text-yellow-800"
                      >
                        <IconInfoCircle size={16} />
                        <Typography variant="caption" size="sm">
                          Simplified view shown. Query contains unsupported keywords or constructs.
                        </Typography>
                      </Stack>
                    )}
                    <SqlLineage
                      details={sqlLineageData.details}
                      tableEdges={sqlLineageData.tableEdges}
                      nodePositions={sqlLineageData.nodePositions || {}}
                      height="50vh"
                      externalSidePanel
                    />
                  </div>
                ) : (
                  <Typography className="al-p-8 al-text-center al-text-gray-500 al-bg-gray-50 al-rounded al-border">
                    No lineage data available for this query
                  </Typography>
                )}
              </TabsContent>
              <TabsContent value="code">{codeEditor}</TabsContent>
            </Tabs>
          </Stack>
        </CardContent>
      </Card>
      <div className="al-bg-white al-p-3 al-my-2 al-rounded-md">
        {/* ``statementId`` is guaranteed non-empty here — the
            loading / error early-returns above only fall through once
            ``useDatabricksQueryDetail`` has resolved successfully, and
            that hook is gated on ``Boolean(statementId)``. The ``!``
            also has type-level effect: ``useParams<{queryId: string}>()``
            is a type *assertion*, react-router still returns
            ``string | undefined`` at runtime — the assertion drops the
            ``undefined`` so the child prop type stays ``string``. A
            stray ``|| ""`` would silently disable
            ``useResourceOpportunities`` (via its
            ``enabled: Boolean(resourceUri)`` guard) instead of failing
            loudly. */}
        <DatabricksQueryOpportunities
          statementId={statementId!}
          workspaceId={query.workspace_id ?? undefined}
        />
      </div>
    </div>
  );
};

export { DatabricksQueryDetailPage };
